import config from '../js/config';

const USERS_EP = 'communal/get-dashboard-users';
const LIST_EP = 'dashboard/list-graphs';
const ALLOCATED_EP = 'dashboard/get-allocated-graphs';
const ADD_EP = 'dashboard/add-graph-user';
const REMOVE_EP = 'dashboard/remove-graph-user';

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function userName(user) {
  return `${user.f_name || ''} ${user.s_name || ''}`.trim();
}

export default {
  users: [],
  usersLoaded: false,
  usersRequest: null,
  catalog: [],
  selectedUser: null,
  pendingRemove: null,

  async prepare() {},

  async render() {
    const input = U.id('allocUserSearch');
    if (!input) return;
    input.addEventListener('focus', () => this.openUserList());
    input.addEventListener('click', () => this.openUserList());
    input.addEventListener('input', () => this.onUserSearch());
    document.addEventListener('click', (event) => {
      const wrap = U.id('allocUserSearchDiv');
      if (!wrap || wrap.contains(event.target)) return;
      this.hideList();
    });
    const list = U.id('allocUserSearchList');
    if (list) {
      list.addEventListener('click', (event) => {
        const item = event.target.closest('li[data-value]');
        if (!item || !item.dataset.value) return;
        const user = (this.users || []).find((row) => row.user_uuid === item.dataset.value);
        if (user) this.chooseUser(user);
      });
    }
    const items = U.id('allocGraphItems');
    if (items) {
      items.addEventListener('click', (event) => {
        const button = event.target.closest('button[data-graph]');
        if (!button) return;
        this.removeGraph(button.dataset.graph, button.dataset.name);
      });
    }
    const addBtn = U.id('allocAddGraphBtn');
    const select = U.id('allocGraphSelect');
    if (addBtn) addBtn.addEventListener('click', () => this.addGraph());
    if (select) select.addEventListener('change', () => this.syncAddButton());
    await Promise.all([this.loadAndShowUsers(), this.loadGraphDropdown()]);
  },

  async loadGraphDropdown() {
    try {
      const parsed = await this.postDashboard(
        LIST_EP,
        { target_uuid: this.selectedUser ? this.selectedUser.user_uuid : null },
        false
      );
      this.catalog = Array.isArray(parsed.data) ? parsed.data : [];
    } catch (error) {
      this.catalog = [];
    }
    this.paintGraphDropdown(this.catalog);
  },

  async loadAndShowUsers() {
    try {
      await this.loadDashboardUsers();
    } catch (error) {
      this.users = [];
    }
  },

  async openUserList() {
    const input = U.id('allocUserSearch');
    if (!input) return;
    try {
      await this.loadDashboardUsers();
    } catch (error) {
      this.paintUserList([]);
      return;
    }
    if (document.activeElement !== input) return;
    this.paintUserList(this.filteredUsers(input.value.trim()));
  },

  filteredUsers(query) {
    const needle = String(query || '').trim().toLowerCase();
    if (!needle) return this.users;
    return this.users.filter((user) => userName(user).toLowerCase().includes(needle));
  },

  paintUserList(users) {
    const list = U.id('allocUserSearchList');
    if (!list) return;
    if (!users.length) {
      list.innerHTML = '<li data-value="">No users found</li>';
      list.hidden = false;
      return;
    }
    list.innerHTML = users
      .map((user) => {
        const name = escapeHtml(userName(user));
        return `<li data-value="${escapeHtml(user.user_uuid)}">${name}</li>`;
      })
      .join('');
    list.hidden = false;
  },

  async postDashboard(path, body, showLoader = true) {
    const hdrs = await O.getAuthHdrs();
    if (showLoader) comp.loader(1);
    try {
      const response = await fetch(`${config.apiURL}${path}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${hdrs.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body || {}),
      });
      const parsed = await response.json();
      if (!response.ok || parsed.status === 'FAILED') {
        const message = parsed?.data?.error || 'Request failed';
        comp.errorBox('FAILED', message);
        throw new Error(message);
      }
      return parsed;
    } finally {
      if (showLoader) comp.loader(0);
    }
  },

  loadDashboardUsers() {
    if (this.usersLoaded) return Promise.resolve(this.users);
    if (!this.usersRequest) {
      this.usersRequest = this.postDashboard(USERS_EP, {}, false)
        .then((parsed) => {
          this.users = Array.isArray(parsed.data) ? parsed.data : [];
          this.usersLoaded = true;
          return this.users;
        })
        .catch((error) => {
          this.usersRequest = null;
          throw error;
        });
    }
    return this.usersRequest;
  },

  paintGraphDropdown(graphs) {
    const select = U.id('allocGraphSelect');
    if (!select) return;
    const rows = Array.isArray(graphs) ? graphs : [];
    const current = select.value;
    select.innerHTML =
      '<option value="">Select a graph</option>' +
      rows
        .map((graph) => {
          const label = graph.short_text || graph.long_text || 'Graph';
          return `<option value="${escapeHtml(graph.graph_uuid)}">${escapeHtml(label)}</option>`;
        })
        .join('');
    if (rows.some((graph) => graph.graph_uuid === current)) select.value = current;
    this.syncAddButton();
  },

  syncAddButton() {
    const select = U.id('allocGraphSelect');
    const addBtn = U.id('allocAddGraphBtn');
    if (!addBtn || !select) return;
    addBtn.disabled = !this.selectedUser || !select.value;
  },

  onUserSearch() {
    const input = U.id('allocUserSearch');
    const query = input ? input.value.trim() : '';
    if (this.selectedUser && userName(this.selectedUser).toLowerCase() !== query.toLowerCase()) {
      this.selectedUser = null;
      this.clearGraphFields();
    }
    if (!this.usersLoaded) return;
    this.paintUserList(this.filteredUsers(query));
  },

  async chooseUser(user) {
    this.selectedUser = user;
    const input = U.id('allocUserSearch');
    if (input) input.value = userName(user);
    this.hideList();
    await Promise.all([this.loadAllocatedGraphs(), this.loadGraphDropdown()]);
  },

  async loadAllocatedGraphs() {
    const items = U.id('allocGraphItems');
    const empty = U.id('allocGraphEmpty');
    if (!items || !empty || !this.selectedUser) return;

    empty.hidden = true;
    items.innerHTML = '';

    try {
      const parsed = await this.postDashboard(ALLOCATED_EP, { target_uuid: this.selectedUser.user_uuid });
      const graphs = Array.isArray(parsed.data) ? parsed.data : [];
      this.paintGraphs(graphs);
    } catch (error) {
      if (empty) {
        empty.hidden = false;
        empty.textContent = 'Unable to load graphs';
      }
    }
  },

  paintGraphs(graphs) {
    const items = U.id('allocGraphItems');
    const empty = U.id('allocGraphEmpty');
    if (!items || !empty) return;
    if (!graphs.length) {
      items.innerHTML = '';
      empty.hidden = false;
      empty.textContent = 'No graphs allocated';
      return;
    }
    empty.hidden = true;
    items.innerHTML = graphs
      .map((graph) => {
        const name = escapeHtml(graph.short_text || graph.long_text || 'Graph');
        return `<li class="alloc-graph-row"><span>${name}</span><button type="button" class="btn alloc-remove" data-graph="${escapeHtml(graph.graph_uuid)}" data-name="${name}" aria-label="Remove ${name}"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" /></svg></button></li>`;
      })
      .join('');
  },

  async addGraph() {
    const select = U.id('allocGraphSelect');
    if (!this.selectedUser || !select || !select.value) return;
    const graphUuid = select.value;
    try {
      await this.postDashboard(ADD_EP, {
        user_uuid: this.selectedUser.user_uuid,
        graph_uuid: graphUuid,
      });
      select.value = '';
      this.syncAddButton();
      await this.loadAllocatedGraphs();
    } catch (error) {
      /* postDashboard already shows the error */
    }
  },

  removeGraph(graphUuid, graphName) {
    if (!graphUuid || !this.selectedUser) return;
    this.pendingRemove = { graphUuid, graphName: graphName || 'this graph' };
    comp.modal(
      'Remove graph',
      `Are you sure you want to remove ${escapeHtml(this.pendingRemove.graphName)} from this user?`,
      'thsPage.confirmRemoveGraph()'
    );
  },

  async confirmRemoveGraph() {
    const pending = this.pendingRemove;
    this.pendingRemove = null;
    if (!pending || !this.selectedUser) return;
    try {
      await this.postDashboard(REMOVE_EP, {
        user_uuid: this.selectedUser.user_uuid,
        graph_uuid: pending.graphUuid,
      });
      await this.loadAllocatedGraphs();
    } catch (error) {
      /* postDashboard already shows the error */
    }
  },

  clearGraphFields() {
    const items = U.id('allocGraphItems');
    const empty = U.id('allocGraphEmpty');
    const addBtn = U.id('allocAddGraphBtn');
    if (items) items.innerHTML = '';
    if (empty) {
      empty.hidden = false;
      empty.textContent = 'No graphs allocated';
    }
    this.paintGraphDropdown(this.catalog);
    if (addBtn) addBtn.disabled = true;
  },

  hideList() {
    const list = U.id('allocUserSearchList');
    if (!list) return;
    list.hidden = true;
    list.innerHTML = '';
  },
};
